// imports
import { AnyAction } from '@reduxjs/toolkit';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import React, {
  useRef, useState, forwardRef,
  useEffect, useImperativeHandle, useMemo
} from 'react';
import {
  ReactZoomPanPinchRef,
  TransformComponent, TransformWrapper
} from 'react-zoom-pan-pinch';

import Layers from './layers';
import Prompt from '../prompt';
import { ICON } from '../../assets';
import { ROUTES } from '../../constants';
import InfoButton from '../../atoms/button/info';
import PrimaryButton from '../../atoms/button/primary';
import { logAnalytics } from '../../services/firebase';
import { setMapRegion } from '../../repositories/store/actions';
import { fetchQuestsProgress } from '../../repositories/store/thunks';
import { selectConfig, selectUIMap, selectUserQuests } from '../../repositories/store/slices';
import {
  IMap, IMapState, IModal,
  QuestStatus, QuestType
} from '../../common/types';
import {
  MIN_SCALE, SCALE_PADDING, ZOOM_FACTOR,
  MAP_DIMENSIONS, ANIMATION_DURATION
} from '../../constants/lib/map';

// interfaces / types / consts
interface Props {
  pinClickedCB?: (regionName: string) => void;
  regionClickedCB?: (regionName: string) => void;
}

// react component
const Map = forwardRef<IMap, Props>((props: Props, ref): JSX.Element => {
  const { pinClickedCB, regionClickedCB } = props;

  // hooks configuration
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const mapInfoPromptRef = useRef<IModal>();
  const wrapperRef = useRef<ReactZoomPanPinchRef>(null);
  const { currRegion } = useSelector(selectUIMap);
  const { world, texts, fetchStatus: configFetchStatus } = useSelector(selectConfig);
  const { data: quests, fetchStatus: questFetchStatus } = useSelector(selectUserQuests);

  const getMapScale = (): number => {
    const widthScale = window.innerWidth / MAP_DIMENSIONS[0];
    const heightScale = window.innerHeight / MAP_DIMENSIONS[1];
    let scale = 0;
    if (widthScale > heightScale) { scale = widthScale; } else { scale = heightScale; }
    if (scale < MIN_SCALE) { scale = MIN_SCALE; }
    return scale + SCALE_PADDING;
  };

  const [initialScale, setInitialScale] = useState(getMapScale());
  const [mapState, setMapState] = useState<IMapState>({
    visibility: false,
    imageQuality: 'Low',
    viewState: 'default'
  });

  useEffect(() => {
    const handleMapScaleChange = (): void => {
      const updatedScale = getMapScale();
      setInitialScale(updatedScale);
      if (mapState.viewState === 'default') { wrapperRef.current?.centerView(updatedScale); }
    };
    window.addEventListener('resize', handleMapScaleChange);
    return () => { window.removeEventListener('resize', handleMapScaleChange); };
  }, [initialScale, mapState]);

  useEffect(() => {
    if (questFetchStatus === 'idle') { dispatch(fetchQuestsProgress() as unknown as AnyAction); }
    if (configFetchStatus === 'success') {
      setMapState((prev) => ({ ...prev, visibility: true }));
      mapInfoPromptRef.current?.show();
    }
    if (currRegion.id) {
      regionClickedCB!(currRegion.id);
      setMapState((prev) => ({ ...prev, viewState: 'zoomedIn', imageQuality: 'High' }));
    }
  }, [configFetchStatus, currRegion.id, questFetchStatus, dispatch, regionClickedCB]);

  const completedQuests = useMemo(() => Object.values(quests).filter((quest) => quest.status === QuestStatus.completed).length, [quests]);
  const questPinIds = useMemo(() => Object.values(quests).map((quest) => {
    const { type, pinId, status } = quest;
    if (type === QuestType.Side) { return pinId; }
    const campaignPlayable = (status !== QuestStatus.locked);
    return campaignPlayable ? pinId : '';
  }).filter((pinId, idx, pinIds) => pinId && pinIds.indexOf(pinId) === idx), [quests]);

  // private methods
  const centerView = (): void => { wrapperRef.current?.centerView(initialScale); };
  const zoomToElement = (regionId: string): void => { wrapperRef.current?.zoomToElement(regionId, initialScale * ZOOM_FACTOR, ANIMATION_DURATION); };
  const zoomIn = (regionId: string): void => {
    if (mapState.viewState === 'default') {
      setMapState((prev) => ({ ...prev, viewState: 'zoomingIn' }));
      wrapperRef.current?.zoomToElement(regionId, initialScale * ZOOM_FACTOR, ANIMATION_DURATION);
      setTimeout(() => {
        if (wrapperRef.current) {
          const { scale, positionX, positionY } = wrapperRef.current.instance.transformState;
          dispatch(setMapRegion({
            id: regionId,
            scale: Number(scale.toFixed(5)),
            position: [positionX, positionY]
          }));
        }
        setMapState((prev) => ({
          ...prev,
          imageQuality: 'High',
          viewState: 'zoomedIn'
        }));
        if (regionClickedCB) {
          regionClickedCB(regionId);
          logAnalytics({ action: 'CLICK', name: 'REGION_PIN', eventParams: { regionId } });
        }
      }, ANIMATION_DURATION + 100);
    }
  };
  const zoomOut = (): void => {
    if (mapState.viewState === 'zoomedIn') {
      setMapState((prev) => ({
        ...prev,
        imageQuality: 'Low',
        viewState: 'zoomingOut'
      }));
      wrapperRef.current?.centerView(initialScale, ANIMATION_DURATION);
      dispatch(setMapRegion({}));
      setTimeout(() => {
        setMapState((prev) => ({ ...prev, viewState: 'default' }));
      }, ANIMATION_DURATION);
    }
  };
  const handlePinClicked = (regionId: string, pinId: string): void => {
    if (pinClickedCB) {
      pinClickedCB(regionId);
      logAnalytics({ action: 'CLICK', name: 'QUEST_PIN', eventParams: { regionId, pinId } });
    }
    navigate(ROUTES.QUESTS, { state: { pinId } });
  };
  useImperativeHandle(ref, () => ({ zoomOut, centerView, zoomToElement }));

  // render
  return (
    <div className="map-container">
      <Prompt title={texts.popup_map_info?.title} message={texts.popup_map_info?.body} ref={mapInfoPromptRef} hideCloseButton />
      {/* <InfoButton className="info-btn" onClick={() => mapInfoPromptRef.current?.show()} /> */}
      <div className="action-buttons">
        <PrimaryButton className="game-btn" onClick={() => navigate(ROUTES.HEROES)} text="Heroes" leftIcon={ICON.HEROES} />
        <PrimaryButton className="game-btn" onClick={() => navigate(ROUTES.BACKPACK)} text="Backpack" leftIcon={ICON.MAP_BACKPACK} />
        <PrimaryButton
          text="Quests"
          leftIcon={ICON.QUESTS}
          badge={completedQuests}
          className="game-btn quests-btn"
          onClick={() => {
            navigate(ROUTES.QUESTS);
            logAnalytics({ action: 'CLICK', name: 'QUEST_BUTTON', eventParams: { completedQuests } });
          }}
        />
      </div>
      <TransformWrapper
        disablePadding
        ref={wrapperRef}
        minScale={initialScale}
        pinch={{ disabled: true }}
        centerOnInit={!currRegion.id}
        doubleClick={{ disabled: true }}
        initialPositionX={currRegion.position?.[0]}
        initialPositionY={currRegion.position?.[1]}
        // disabled={mapState.viewState !== 'default'}
        disabled
        initialScale={currRegion.scale || initialScale}
        wheel={{ disabled: true, touchPadDisabled: true }}
      >
        <TransformComponent wrapperClass={`wrapper ${mapState.visibility ? 'show' : ''}`}>
          <Layers
            world={world}
            questPinIds={questPinIds}
            mapViewState={mapState.viewState}
            imageQuality={mapState.imageQuality}
            onRegionClicked={zoomIn}
            onPinClicked={handlePinClicked}
          />
        </TransformComponent>
      </TransformWrapper>
    </div>
  );
});

// display name
Map.displayName = 'Map';

// exports
export default Map;

// default props
Map.defaultProps = {
  pinClickedCB: () => null,
  regionClickedCB: () => null
};
